import { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sparkles, Search, Pencil, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import api from '@/lib/api';

export interface ArcheSelection {
  archeId: string | null;
  name: string | null;
  imageRef: string | null;
}

interface ArcheResult {
  archeId: string;
  name: string;
  imageRef: string | null;
}

interface TicketArchePickerProps {
  /** Currently-selected arche id, or null for "Fias system". */
  value: string | null;
  /** Display name for the current value (null when value is null/unknown). */
  displayName: string | null;
  /** Icon ref for the current value, if any. */
  displayImageRef: string | null;
  onChange: (next: ArcheSelection) => void;
}

/**
 * Searchable arche picker used to (re)tag the arche a ticket concerns. Searches
 * ALL arches (not just the admin's worktop), since a ticket can reference any
 * arche. "Fias system" (null) is always offered. The current selection renders
 * as a chip; clicking "Change" opens a debounced server-side search.
 */
export function TicketArchePicker({
  value,
  displayName,
  displayImageRef,
  onChange,
}: TicketArchePickerProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ArcheResult[]>([]);
  const [loading, setLoading] = useState(false);

  // Debounced server search; only runs while the popover is open and the term
  // is long enough to be meaningful.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .searchArchesForSupportAdmin(term)
        .then((r) => setResults(r.arches))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [q, open]);

  const select = (next: ArcheSelection) => {
    onChange(next);
    setOpen(false);
    setQ('');
    setResults([]);
  };

  return (
    <div className="flex items-center gap-2">
      {/* Current selection chip */}
      {value ? (
        <span
          className="inline-flex max-w-[160px] items-center gap-1.5 rounded-full border border-border bg-background px-2 py-0.5 text-xs"
          title={displayName ?? value}
        >
          {displayImageRef ? (
            <ArcheIconImg
              imageRef={displayImageRef}
              size={64}
              alt=""
              className="h-4 w-4 rounded-sm"
            />
          ) : (
            <DefaultArcheIcon className="h-4 w-4 rounded-sm" />
          )}
          <span className="truncate">{displayName ?? 'Unknown arche'}</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground">
          <Sparkles className="h-3 w-3" />
          Fias system
        </span>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
            <Pencil className="h-3 w-3 mr-1" />
            Change
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2" align="start">
          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search arches..."
              className="h-8 pl-7 text-sm"
              maxLength={100}
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            <button
              type="button"
              onClick={() => select({ archeId: null, name: null, imageRef: null })}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                value === null && 'bg-muted',
              )}
            >
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              Fias system
            </button>
            {loading && (
              <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Searching…
              </div>
            )}
            {!loading && q.trim().length >= 2 && results.length === 0 && (
              <div className="px-2 py-2 text-xs text-muted-foreground">No arches found.</div>
            )}
            {!loading &&
              results.map((a) => (
                <button
                  key={a.archeId}
                  type="button"
                  onClick={() => select({ archeId: a.archeId, name: a.name, imageRef: a.imageRef })}
                  className={cn(
                    'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                    value === a.archeId && 'bg-muted',
                  )}
                >
                  {a.imageRef ? (
                    <ArcheIconImg
                      imageRef={a.imageRef}
                      size={64}
                      alt=""
                      className="h-4 w-4 rounded-sm"
                    />
                  ) : (
                    <DefaultArcheIcon className="h-4 w-4 rounded-sm" />
                  )}
                  <span className="truncate">{a.name}</span>
                </button>
              ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
